<template>
  <div class="water-bills-card">
    <!-- Card Header -->
    <div class="card-header">
      <div class="header-left">
        <div class="header-icon">
          <i class="bi bi-receipt"></i>
        </div>
        <div>
          <h3 class="card-title">Water Bill Statements</h3>
          <p class="card-subtitle">
            Current and historical water bills
            <span class="payment-info-note">
              <i class="bi bi-info-circle"></i>
              Online payments may take up to 24 hours to be posted. Receipts will be available once payment is
              confirmed.
            </span>
          </p>
        </div>
      </div>
      <div class="header-right">
        <span class="badge bills-count">{{ filteredBills.length }} bill(s)</span>
        <span class="badge unpaid-count" v-if="unpaidBillsCount > 0">
          {{ unpaidBillsCount }} unpaid
        </span>
      </div>
    </div>

    <!-- Bills Summary Stats -->
    <div v-if="filteredBills.length > 0" class="bills-summary">
      <div class="stats-grid">
        <div class="stat-item">
          <div class="stat-icon overdue">
            <i class="bi bi-exclamation-triangle"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">Total Due</div>
            <div class="stat-value">₱{{ formatCurrency(totalAmountDue) }}</div>
          </div>
        </div>

        <div class="stat-item">
          <div class="stat-icon pending">
            <i class="bi bi-clock"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">Overdue Bills</div>
            <div class="stat-value">{{ overdueBillsCount }}</div>
          </div>
        </div>

        <div class="stat-item">
          <div class="stat-icon paid">
            <i class="bi bi-check-circle"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">Paid Bills</div>
            <div class="stat-value">{{ paidBillsCount }}</div>
          </div>
        </div>

        <div class="stat-item">
          <div class="stat-icon upcoming">
            <i class="bi bi-calendar-event"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">Upcoming</div>
            <div class="stat-value">{{ upcomingBillsCount }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bills List -->
    <div v-if="filteredBills.length > 0" class="bills-list">
      <div class="bills-table">
        <div class="table-header">
          <div class="table-col col-period">Account Name</div>
          <div class="table-col col-amount">Amount</div>
          <div class="table-col col-due">Due Date</div>
          <div class="table-col col-status">Status</div>
          <div class="table-col col-actions">Actions</div>
        </div>

        <div v-for="(bill, index) in filteredBills" :key="`bill${index}`" class="bill-item"
          :class="getBillStatusClass(bill)">
          <div class="table-col col-period">
            <div class="period-range">
              {{ bill.accountName?.toUpperCase() }}
            </div>
            <div class="bill-number" v-if="bill.userPK_CtrctTrans">
              Bill #{{ bill.userPK_CtrctTrans }}
            </div>
          </div>

          <div class="table-col col-amount">
            <div class="amount-main">₱{{ formatCurrency(bill.billAmount_CtrctTrans) }}</div>
            <div class="amount-breakdown" v-if="bill.breakdown">
              <div class="breakdown-item" v-for="(value, key) in bill.breakdown" :key="key">
                <span class="breakdown-label">{{ key }}:</span>
                <span class="breakdown-value">₱{{ formatCurrency(value) }}</span>
              </div>
            </div>
          </div>

          <div class="table-col col-due">
            <div class="due-date">{{ formatDate(bill.dueDate_CtrctTrans) }}</div>
            <div class="due-status" :class="getDueStatusClass(bill)">
              {{ getDueStatusText(bill) }}
            </div>
          </div>

          <div class="table-col col-status">
            <span class="status-badge" :class="getStatusBadgeClass(bill)">
              {{ bill.statusBalanceClr_CtrctTrans || 'Pending' }}
            </span>
            <div class="payment-date" v-if="bill.paymentDate">
              Paid: {{ formatDate(bill.paymentDate) }}
            </div>
          </div>

          <div class="table-col col-actions">
            <div class="action-buttons">
              <button v-if="canPay(bill)" @click="$emit('pay-now', bill)" class="btn-pay-now"
                :disabled="isProcessingPayment">
                <i class="bi bi-lightning-charge"></i>
                Pay Now
              </button>

              <!-- <button @click="$emit('view-details', bill)" class="btn-details">
                <i class="bi bi-eye"></i>
                Details
              </button> -->

              <!-- <button v-if="bill.statusBalanceClr_CtrctTrans === 'Fully Paid'" @click="$emit('view-receipt', bill)"
                class="btn-receipt">
                <i class="bi bi-receipt"></i>
                Receipt
              </button> -->


            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <div class="empty-icon">
        <i class="bi bi-receipt"></i>
      </div>
      <h4>No Water Bills</h4>
      <p>No water bills found for this account.</p>
    </div>

    <!-- Quick Actions Footer -->
    <div class="card-footer">
      <div class="footer-actions">
        <button v-if="unpaidBillsCount > 0" @click="$emit('pay-all', unpaidBills)" class="btn-pay-all"
          :disabled="isProcessingPayment">
          <i class="bi bi-wallet2"></i>
          Pay All Unpaid (₱{{ formatCurrency(totalAmountDue) }})
        </button>

        <div class="footer-filters">
          <label class="filter-label">Show:</label>
          <select v-model="filterStatus" class="filter-select">
            <option value="all">All Bills</option>
            <option value="unpaid">Unpaid Only</option>
            <option value="overdue">Overdue</option>
            <option value="paid">Paid</option>
          </select>

          <select v-model="sortBy" class="filter-select">
            <option value="dueDate">Sort by Due Date</option>
            <option value="amount">Sort by Amount</option>
            <option value="period">Sort by Period</option>
          </select>
        </div>
      </div>

      <div class="footer-total">
        <div class="total-summary">
          <span class="total-label">Total Outstanding:</span>
          <span class="total-value">₱{{ formatCurrency(totalAmountDue) }}</span>
        </div>
        <div class="payment-reminder" v-if="unpaidBillsCount > 0">
          <i class="bi bi-info-circle"></i>
          Payment due in {{ getNextDueDays }} days
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
//eslint-disable-next-line
const props = defineProps({
  wbills: {
    type: Array,
    required: true,
    default: () => []
  },
  isProcessingPayment: {
    type: Boolean,
    default: false
  }
})
//eslint-disable-next-line
const emit = defineEmits([
  'pay-now',
  'pay-all',
  'view-details',
  'view-receipt',
  'download-bill'
])
//eslint-disable-next-line
const canPay = (bill) => {
  if (bill.statusBalanceClr_CtrctTrans !== 'Fully Paid' && bill.billAmount_CtrctTrans > 0
  ) {
    const idx = props.wbills.findIndex(x => x.strSysPK === bill.strSysPK);
    if (idx === -1) {
      return false;
    }
    if (idx + 1 < props.wbills.length) {
      if (props.wbills[idx + 1].statusBalanceClr_CtrctTrans === 'Fully Paid' || bill.billAmount_CtrctTrans === 0)
        return true;
      return false;
    }
    else {
      return true;
    }
  }

  return false;
}

// Reactive filters
const filterStatus = ref('all')
const sortBy = ref('dueDate')

// Computed properties
const filteredBills = computed(() => {
  let bills = [...props.wbills]

  // Apply status filter
  if (filterStatus.value !== 'all') {
    bills = bills.filter(bill => {
      if (filterStatus.value === 'unpaid') {
        return bill.statusBalanceClr_CtrctTrans !== 'Fully Paid' && bill.billAmount_CtrctTrans > 0
      } else if (filterStatus.value === 'overdue') {
        return isBillOverdue(bill)
      } else if (filterStatus.value === 'paid') {
        return bill.statusBalanceClr_CtrctTrans === 'Fully Paid'
      }
      return true
    })
  }

  // Apply sorting
  bills.sort((a, b) => {
    if (sortBy.value === 'dueDate') {
      return new Date(a.dueDate || 0) - new Date(b.dueDate || 0)
    } else if (sortBy.value === 'amount') {
      return b.billAmount_CtrctTrans - a.billAmount_CtrctTrans
    } else if (sortBy.value === 'period') {
      return new Date(b.billingPeriodStart || 0) - new Date(a.billingPeriodStart || 0)
    }
    return 0
  })

  return bills
})

const unpaidBills = computed(() => {
  return props.wbills.filter(bill => bill.statusBalanceClr_CtrctTrans !== 'Fully Paid' && bill.billAmount_CtrctTrans > 0)
})

const unpaidBillsCount = computed(() => unpaidBills.value.length)

const totalAmountDue = computed(() => {
  return unpaidBills.value.reduce((sum, bill) => sum + (parseFloat(bill.billAmount_CtrctTrans) || 0), 0)
})

const overdueBillsCount = computed(() => {
  return props.wbills.filter(bill => isBillOverdue(bill)).length
})

const paidBillsCount = computed(() => {
  return props.wbills.filter(bill => bill.statusBalanceClr_CtrctTrans === 'Fully Paid').length
})

const upcomingBillsCount = computed(() => {
  return props.wbills.filter(bill => {
    if (!bill.dueDate_CtrctTrans || bill.statusBalanceClr_CtrctTrans === 'Fully Paid') return false
    const dueDate = new Date(bill.dueDate)
    const today = new Date()
    const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24))
    return daysUntilDue > 0 && daysUntilDue <= 30
  }).length
})

const getNextDueDays = computed(() => {
  const unpaid = unpaidBills.value
  if (unpaid.length === 0) return 0

  const nearestDue = Math.min(...unpaid
    .map(bill => {
      if (!bill.dueDate) return Infinity
      const dueDate = new Date(bill.dueDate)
      const today = new Date()
      return Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24))
    })
    .filter(days => days > 0)
  )

  return isFinite(nearestDue) ? nearestDue : 0
})

// Methods
const formatCurrency = (amount) => {
  if (!amount) return '0.00'
  return parseFloat(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  } catch {
    return dateString
  }
}

const isBillOverdue = (bill) => {
  if (bill.status === 'Paid') return false
  if (!bill.dueDate) return false

  const dueDate = new Date(bill.dueDate)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return dueDate < today && bill.billAmount_CtrctTrans > 0
}

const getBillStatusClass = (bill) => {
  if (bill.status === 'Paid') return 'status-paid'
  if (isBillOverdue(bill)) return 'status-overdue'
  if (bill.billAmount_CtrctTrans > 0) return 'status-unpaid'
  return 'status-pending'
}

const getStatusBadgeClass = (bill) => {
  if (bill.status === 'Paid') return 'badge-paid'
  if (isBillOverdue(bill)) return 'badge-overdue'
  if (bill.billAmount_CtrctTrans > 0) return 'badge-unpaid'
  return 'badge-pending'
}

const getDueStatusClass = (bill) => {
  if (bill.status === 'Paid') return 'due-paid'
  if (isBillOverdue(bill)) return 'due-overdue'

  if (bill.dueDate) {
    const dueDate = new Date(bill.dueDate)
    const today = new Date()
    const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24))

    if (daysUntilDue <= 7) return 'due-soon'
    if (daysUntilDue <= 30) return 'due-upcoming'
  }

  return 'due-normal'
}

const getDueStatusText = (bill) => {
  if (bill.statusBalanceClr_CtrctTrans === 'Fully Paid') return 'Paid'
  if (isBillOverdue(bill)) return 'Overdue'

  if (bill.dueDate_CtrctTranse) {
    const dueDate = new Date(bill.dueDate_CtrctTrans)
    const today = new Date()
    const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24))

    if (daysUntilDue <= 0) return 'Due Today'
    if (daysUntilDue === 1) return 'Due Tomorrow'
    if (daysUntilDue <= 7) return `Due in ${daysUntilDue} days`
    if (daysUntilDue <= 30) return 'Due Soon'
  }

  return 'Upcoming'
}
</script>

<style scoped>
.water-bills-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

/* Card Header - Red Theme */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-icon {
  font-size: 2rem;
  opacity: 0.9;
}

.card-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.card-subtitle {
  margin: 0.25rem 0 0 0;
  opacity: 0.9;
  font-size: 0.875rem;
}

.bills-count,
.unpaid-count {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.5rem;
}

.unpaid-count {
  background: rgba(255, 255, 255, 0.3);
  animation: pulse 2s infinite;
}

/* Bills Summary */
.bills-summary {
  padding: 1.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.stat-item:hover {
  border-color: #dc3545;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.1);
  transform: translateY(-2px);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.stat-icon.overdue {
  background: #fff5f5;
  color: #dc3545;
}

.stat-icon.pending {
  background: #fff3cd;
  color: #856404;
}

.stat-icon.paid {
  background: #d4edda;
  color: #155724;
}

.stat-icon.upcoming {
  background: #e7f1ff;
  color: #0d6efd;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3e50;
}

/* Bills List */
.bills-list {
  padding: 0;
}

.payment-info-note {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.75rem;
  padding: 0.25rem 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  font-size: 0.75rem;
  opacity: 0.9;
}

.payment-info-note i {
  font-size: 0.875rem;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1fr 1.5fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
}

.bill-item {
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1fr 1.5fr;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  transition: all 0.3s ease;
  align-items: center;
}

.bill-item:hover {
  background: #f8f9fa;
}

.bill-item.status-overdue {
  background: #fff5f5;
  border-left: 4px solid #dc3545;
}

.bill-item.status-unpaid {
  background: #fffcf3;
  border-left: 4px solid #ffc107;
}

.bill-item.status-paid {
  background: #f8fff9;
  border-left: 4px solid #28a745;
  opacity: 0.9;
}

.table-col {
  padding: 0.25rem 0;
}

.col-period .period-range {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.25rem;
}

.col-period .bill-number {
  font-size: 0.75rem;
  color: #6c757d;
}

.col-amount .amount-main {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.amount-breakdown {
  font-size: 0.75rem;
  color: #6c757d;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.125rem;
}

.breakdown-label {
  font-weight: 500;
}

.breakdown-value {
  font-weight: 600;
}

.col-due .due-date {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.25rem;
}

.due-status {
  font-size: 0.75rem;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  display: inline-block;
  font-weight: 600;
}

.due-overdue {
  background: #f8d7da;
  color: #721c24;
}

.due-soon {
  background: #fff3cd;
  color: #856404;
}

.due-upcoming {
  background: #e7f1ff;
  color: #0d6efd;
}

.due-paid {
  background: #d4edda;
  color: #155724;
}

.col-status .status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

.badge-paid {
  background: #d4edda;
  color: #155724;
}

.badge-overdue {
  background: #f8d7da;
  color: #721c24;
  animation: pulse 2s infinite;
}

.badge-unpaid {
  background: #fff3cd;
  color: #856404;
}

.badge-pending {
  background: #e2e3e5;
  color: #383d41;
}

.col-status .payment-date {
  font-size: 0.75rem;
  color: #6c757d;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.btn-pay-now {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(220, 53, 69, 0.3);
  font-size: 0.875rem;
}

.btn-pay-now:hover:not(:disabled) {
  background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(220, 53, 69, 0.4);
}

.btn-pay-now:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #6c757d;
  box-shadow: none;
}

.btn-details,
.btn-receipt,
.btn-download {
  background: transparent;
  color: #6c757d;
  border: 1px solid #dee2e6;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  min-width: 36px;
  justify-content: center;
}

.btn-details:hover,
.btn-receipt:hover {
  background: #f8f9fa;
  border-color: #dc3545;
  color: #dc3545;
}

.btn-download:hover {
  background: #f8f9fa;
  border-color: #0d6efd;
  color: #0d6efd;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem 2rem;
}

.empty-icon {
  font-size: 3rem;
  color: #dc3545;
  opacity: 0.3;
  margin-bottom: 1rem;
}

.empty-state h4 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: #6c757d;
  margin-bottom: 1.5rem;
}

/* Card Footer */
.card-footer {
  padding: 1.5rem;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.footer-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.btn-pay-all {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.btn-pay-all:hover:not(:disabled) {
  background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4);
}

.btn-pay-all:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #6c757d;
  box-shadow: none;
}

.footer-filters {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.filter-label {
  font-weight: 600;
  color: #495057;
}

.filter-select {
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: white;
  color: #495057;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.filter-select:hover {
  border-color: #dc3545;
}

.footer-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px dashed #dee2e6;
}

.total-summary {
  display: flex;
  align-items: baseline;
  gap: 1rem;
}

.total-label {
  font-size: 1rem;
  color: #495057;
  font-weight: 600;
}

.total-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #dc3545;
}

.payment-reminder {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #dc3545;
  font-weight: 500;
  background: #fff5f5;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: 1px solid #f8d7da;
}

/* Animations */
@keyframes pulse {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.7;
  }

  100% {
    opacity: 1;
  }
}

/* Responsive Design */
@media (max-width: 1024px) {

  .table-header,
  .bill-item {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .table-col {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .table-col::before {
    content: attr(data-label);
    font-weight: 600;
    color: #495057;
    font-size: 0.875rem;
  }

  .col-period::before {
    content: 'Billing Period';
  }

  .col-amount::before {
    content: 'Amount';
  }

  .col-due::before {
    content: 'Due Date';
  }

  .col-status::before {
    content: 'Status';
  }

  .col-actions::before {
    content: 'Actions';
  }
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }

  .header-left {
    flex-direction: column;
    text-align: center;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .footer-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .footer-filters {
    flex-direction: column;
    align-items: stretch;
  }

  .footer-total {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  .action-buttons {
    justify-content: center;
  }
}
</style>